import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaGavel, FaClock, FaTruck, FaWeightHanging,
  FaMapMarkerAlt, FaMoneyBillWave, FaCalendarAlt,
  FaSpinner, FaEye, FaCheckCircle, FaTimes,
  FaPlus, FaPaperPlane, FaEdit, FaUsers
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';

function SubbieTenders({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [tenders, setTenders] = useState([]);
  const [myBids, setMyBids] = useState({});
  const [user, setUser] = useState({});
  const [subbieId, setSubbieId] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    getSubbieId(userData);
  }, []);

  const getSubbieId = async (userData) => {
    try {
      let savedId = localStorage.getItem('subbieId');
      if (savedId) {
        setSubbieId(savedId);
        fetchTenders(savedId);
        return;
      }

      const response = await fetch(`${API_URL}/subcontractors?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.subcontractors && data.subcontractors.length > 0) {
        const id = data.subcontractors[0].id;
        setSubbieId(id);
        localStorage.setItem('subbieId', id);
        fetchTenders(id);
      }
    } catch (err) {
      console.error('Error fetching subbie ID:', err);
      setLoading(false);
    }
  };

  const fetchTenders = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/tenders/open`);
      const data = await response.json();
      if (data.success) {
        setTenders(data.tenders || []);
        // Fetch my bids for each tender
        for (const tender of data.tenders) {
          const bidRes = await fetch(`${API_URL}/tenders/${tender.id}/my-bid?subcontractor_id=${id}`);
          const bidData = await bidRes.json();
          if (bidData.success && bidData.bid) {
            setMyBids(prev => ({ ...prev, [tender.id]: bidData.bid }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching tenders:', err);
      toast.error('Failed to load tenders');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="tenders" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading tenders...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  return (
    <SubbieLayout activeTab="tenders" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
          <FaGavel className="inline mr-2" style={{ color: '#00A86B' }} />
          Tender Board
        </h1>
        <p className="text-sm" style={{ color: '#4A5568' }}>Browse open tenders and submit your bids</p>
      </div>

      {tenders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold mb-2" style={{ color: '#0A1929' }}>No Open Tenders</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>Check back later for new opportunities.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tenders.map((tender) => {
            const myBid = myBids[tender.id];
            const job = tender.jobs;
            const isExpired = new Date(tender.bidding_deadline) < new Date();

            return (
              <div key={tender.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-navy">{tender.tender_number}</h3>
                      {tender.tender_type === 'framework' && (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-600">
                          Framework
                        </span>
                      )}
                      {myBid ? (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-600">
                          Your Bid: R{myBid.bid_rate}/MT
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-600">
                          Open for Bidding
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {job?.commodity || 'Unknown'} • {tender.total_volume_mt || 0} MT
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-2">
                      <span className="flex items-center gap-1">
                        <FaMapMarkerAlt size={14} /> {job?.origin} → {job?.destination}
                      </span>
                      <span className="flex items-center gap-1">
                        <FaCalendarAlt size={14} /> Deadline: {formatDate(tender.bidding_deadline)}
                        {isExpired && <span className="text-red-500 ml-1">(Expired)</span>}
                      </span>
                      {tender.target_rate && (
                        <span className="flex items-center gap-1">
                          <FaMoneyBillWave size={14} /> Target: R{tender.target_rate}/MT
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <FaUsers size={14} /> Winners: {tender.number_of_winners}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/subbie-tenders/${tender.id}/bid`}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                        isExpired 
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                          : 'bg-green text-white hover:bg-green'
                      }`}
                      onClick={(e) => {
                        if (isExpired) {
                          e.preventDefault();
                          toast.warning('This tender has expired');
                        }
                      }}
                    >
                      {myBid ? <FaEdit size={14} /> : <FaPlus size={14} />}
                      {myBid ? 'Update Bid' : 'Submit Bid'}
                    </Link>
                    <Link
                      to={`/subbie-tenders/${tender.id}`}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:border-green hover:text-green transition-colors"
                    >
                      <FaEye size={14} /> View
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SubbieLayout>
  );
}

export default SubbieTenders;
